import React, { useEffect, useState, useRef } from 'react';
import {
  Container,
  Grid,
  Paper,
  Toolbar,
  Stack,
  Backdrop,
  Typography,
  CircularProgress,
  Divider,
  Button,
  Breadcrumbs,
  Link,
  Box,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';

import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useParams, useNavigate } from 'react-router';
import { useUser, useGpuClasses } from '@/providers';
import { isAuthFailed } from '@/services/auth';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import ReplayIcon from '@mui/icons-material/Replay';
import CompressIcon from '@mui/icons-material/Compress';
import DeleteIcon from '@mui/icons-material/Delete';
import ListIcon from '@mui/icons-material/List';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import { updateJob, fetchJob, terminateJob, fetchJobs } from '@/actions/jobs';
import getGpuTypeName from '../getGpuTypeName';
import JobWorker from './JobWorker';
import STATUSES from '@/components/statuses';
import JOB_TYPES from '../types';
import api from '@/apis/api';
import NoLogsModal from '../NoLogsModal';
import toNiceTimestamp from '@/util/toNiceTimestamp';

export default function JobDetails() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { type: typeParam, job_uuid } = useParams();

  const api_error = useSelector((state) => state.errorMessage);
  const jobs = useSelector((state) => state.jobs || []);
  const gpuClasses = useGpuClasses() || [];
  const user = useUser();

  const job = jobs.find((job) => job.job_uuid === job_uuid);
  const jobType = Object.entries(JOB_TYPES)
    .map(([key, value]) => {
      return value.value === typeParam ? value : undefined;
    })
    .filter((type) => type)[0];

  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [starting, setStarting] = useState(false);
  const [terminating, setTerminating] = useState(false);
  const [log_error, setLogError] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const timer_id_ref = useRef(null);
  const timer_interval_ref = useRef(null);

  useEffect(() => {
    (async () => {
      if (!job) {
        if (user?.selected_project) {
          await dispatch(fetchJobs(user.selected_project));
        }
      }
      // GpuClasses are fetched by Layout via context
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!job) {
      return;
    }

    const { status, job_uuid, workers } = job;

    const poll_job = () => {
      if (!user?.selected_project || isAuthFailed()) {
        return;
      }
      Promise.resolve(
        dispatch(fetchJob(user.selected_project, job_uuid)),
      ).catch(() => {
        // Auth recovery and the action's error snackbar handle this.
      });
    };

    const ensureTimer = () => {
      if (status === STATUSES.FAILED) {
        if (timer_id_ref.current) {
          clearInterval(timer_id_ref.current);
          timer_interval_ref.current = null;
          timer_id_ref.current = null;
        }
      } else if (
        [
          STATUSES.NEW,
          STATUSES.STOPPING,
          STATUSES.STARTING,
          STATUSES.PROVISIONING,
          STATUSES.WAITING_DATA,
          STATUSES.WAITING_GPU,
          STATUSES.UPLOADING,
          STATUSES.REMOVING,
          STATUSES.WAITING_RESTORE,
          STATUSES.RESTORING,
          STATUSES.SAVING,
          STATUSES.UPDATING,
          STATUSES.COPYING,
          STATUSES.COMPRESSING,
        ].includes(status)
      ) {
        // Job is in a transition state
        if (!timer_id_ref.current) {
          timer_interval_ref.current = 2000;
          timer_id_ref.current = setInterval(
            poll_job,
            timer_interval_ref.current,
          );
        } else if (timer_interval_ref.current !== 2000) {
          clearInterval(timer_id_ref.current);
          timer_interval_ref.current = 2000;
          timer_id_ref.current = setInterval(
            poll_job,
            timer_interval_ref.current,
          );
        }
      } else if (
        workers &&
        workers
          .filter((worker) => worker != null)
          .map((worker) => {
            return [
              STATUSES.NEW,
              STATUSES.STOPPING,
              STATUSES.STARTING,
              STATUSES.PROVISIONING,
              STATUSES.REMOVING,
              STATUSES.WAITING_RESTORE,
              STATUSES.RESTORING,
              STATUSES.SAVING,
              STATUSES.UPDATING,
              STATUSES.COPYING,
              STATUSES.COMPRESSING,
              STATUSES.UPLOADING,
              STATUSES.WAITING_DATA,
              STATUSES.WAITING_GPU,
            ].includes(worker?.status);
          })
          .includes(true)
      ) {
        if (!timer_id_ref.current) {
          timer_interval_ref.current = 2000;
          timer_id_ref.current = setInterval(
            poll_job,
            timer_interval_ref.current,
          );
        } else if (timer_interval_ref.current !== 2000) {
          clearInterval(timer_id_ref.current);
          timer_interval_ref.current = 2000;
          timer_id_ref.current = setInterval(
            poll_job,
            timer_interval_ref.current,
          );
        }
      } else if (status === STATUSES.RUNNING) {
        // job is running, so we need to keep tabs on it slowly
        if (!timer_id_ref.current) {
          timer_interval_ref.current = 30000;
          timer_id_ref.current = setInterval(
            poll_job,
            timer_interval_ref.current,
          );
        } else if (timer_interval_ref.current !== 30000) {
          clearInterval(timer_id_ref.current);
          timer_interval_ref.current = 30000;
          timer_id_ref.current = setInterval(
            poll_job,
            timer_interval_ref.current,
          );
        }
      } else if (timer_id_ref.current) {
        clearInterval(timer_id_ref.current);
        timer_id_ref.current = null;
        timer_interval_ref.current = null;
      }
    };

    ensureTimer();

    return () => {
      if (timer_id_ref.current) {
        clearInterval(timer_id_ref.current);
      }
    };
  }, [job, dispatch, user?.selected_project]);

  const handleDownload = async () => {
    if (!job || !user) return;
    const validWorkers =
      job.workers?.filter(
        (worker) => worker != null && worker.job_worker_uuid,
      ) || [];
    if (validWorkers.length === 0) return;
    setDownloading(true);
    try {
      let response = await api.get(
        `/job/${job.job_uuid}/worker/${validWorkers[0].job_worker_uuid}/logs`,
        { params: { project_uuid: user.selected_project } },
      );
      window.location.href = response.data;
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setLogError(true);
      }
    }
    setDownloading(false);
  };

  const handleStop = async () => {
    if (!job || !user?.selected_project) return;
    setStopping(true);
    await dispatch(
      updateJob(user.selected_project, job.job_uuid, { command: 'stop' }),
    );
    setStopping(false);
  };

  const handleCompress = async () => {
    if (!job) return;
    setCompressing(true);
    if (user?.selected_project) {
      await dispatch(
        updateJob(user.selected_project, job.job_uuid, { command: 'compress' }),
      );
    }
    setCompressing(false);
  };

  const handleRestart = async () => {
    if (!job || !user?.selected_project) return;
    setStarting(true);
    await dispatch(
      updateJob(user.selected_project, job.job_uuid, { command: 'start' }),
    );
    setStarting(false);
  };

  const handleTerminate = async () => {
    if (!job) return;
    setTerminating(true);
    if (user?.selected_project) {
      await dispatch(terminateJob(user.selected_project, job.job_uuid));
    }
    navigate(`/jobs/${job.type}`);
  };

  const handleOpen = async () => {
    if (!job || !job.endpoint) return;
    const win = window.open(
      `${job.endpoint.url}/?token=${job.endpoint.token}`,
      '_blank',
    );
    if (win != null) {
      win.focus();
    }
  };

  const copyCodeToClipboard = (code) => {
    navigator.clipboard.writeText(code).then(() => setCopySuccess(true));
  };

  if (loading) {
    return (
      <Backdrop open={loading}>
        <CircularProgress />
      </Backdrop>
    );
  }

  if (!job || !jobType) {
    return null;
  }

  return (
    <Container>
      <Breadcrumbs aria-label='breadcrumb'>
        <Link
          underline='hover'
          color='inherit'
          component={RouterLink}
          to='/dashboard'
        >
          Home
        </Link>
        <Link
          underline='hover'
          color='inherit'
          component={RouterLink}
          to={`/jobs/${jobType.value}`}
        >
          {jobType.label}
        </Link>
        <Typography color='text.primary'>{job.name}</Typography>
      </Breadcrumbs>
      <br />
      <Paper sx={{ display: 'flex', width: '100%' }}>
        <Grid container spacing={4} sx={{ flexGrow: 1 }}>
          <Grid size={{ xs: 12, md: 12, lg: 12 }}>
            <Paper sx={{ display: 'flex', width: '100%' }}>
              <Toolbar>
                <Stack spacing={2} direction='row' alignItems='center'>
                  <Typography variant='h5'>{job.name}</Typography>
                  {job.type === JOB_TYPES.NOTEBOOK.value ? (
                    <Button
                      disabled={job.status !== STATUSES.RUNNING}
                      onClick={handleOpen}
                    >
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <OpenInNewIcon />
                        <Typography variant='button' display='block'>
                          Open
                        </Typography>
                      </Stack>
                    </Button>
                  ) : undefined}
                  <Button
                    disabled={
                      ![
                        STATUSES.RUNNING,
                        STATUSES.UPLOADING,
                        STATUSES.WAITING_GPU,
                      ].includes(job.status) || stopping
                    }
                    onClick={handleStop}
                  >
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <StopCircleIcon />
                      <Typography variant='button' display='block'>
                        Stop
                      </Typography>
                    </Stack>
                  </Button>
                  {job.type === JOB_TYPES.NOTEBOOK.value ? (
                    <Button
                      disabled={
                        job.status !== STATUSES.STOPPED ||
                        starting ||
                        terminating
                      }
                      onClick={handleRestart}
                    >
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <ReplayIcon />
                        <Typography variant='button' display='block'>
                          Restart
                        </Typography>
                      </Stack>
                    </Button>
                  ) : undefined}
                  {job.type === JOB_TYPES.NOTEBOOK.value ? (
                    <Button
                      disabled={
                        job.status !== STATUSES.STOPPED ||
                        compressing ||
                        terminating
                      }
                      onClick={handleCompress}
                    >
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <CompressIcon />
                        <Typography variant='button' display='block'>
                          Compress
                        </Typography>
                      </Stack>
                    </Button>
                  ) : undefined}
                  <Button
                    disabled={
                      ![
                        STATUSES.FAILED,
                        STATUSES.STOPPED,
                        STATUSES.FINISHED,
                        STATUSES.CANCELED,
                        STATUSES.WAITING_DATA,
                        STATUSES.WAITING_RESTORE,
                      ].includes(job.status) || terminating
                    }
                    onClick={handleTerminate}
                  >
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <DeleteIcon />
                      <Typography variant='button' display='block'>
                        Terminate
                      </Typography>
                    </Stack>
                  </Button>
                  {job.type !== JOB_TYPES.NOTEBOOK.value ? (
                    <Button
                      component={RouterLink}
                      to={{
                        pathname: `/jobs/${jobType.value}/logs/${job.job_uuid}`,
                        state: {
                          job_uuid: job.job_uuid,
                          job_name: job.name,
                        },
                      }}
                    >
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <ListIcon />
                        <Typography variant='button' display='block'>
                          View Logs
                        </Typography>
                      </Stack>
                    </Button>
                  ) : undefined}
                  {job.workers &&
                  job.workers.filter(
                    (worker) => worker != null && worker.job_worker_uuid,
                  ).length === 1 &&
                  job.type !== JOB_TYPES.NOTEBOOK.value ? (
                    <Button
                      disabled={
                        ![STATUSES.FINISHED, STATUSES.STOPPED].includes(
                          job.status,
                        ) || downloading
                      }
                      onClick={handleDownload}
                    >
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <CloudDownloadIcon />
                        <Typography variant='button' display='block'>
                          Download Logs
                        </Typography>
                      </Stack>
                    </Button>
                  ) : undefined}
                </Stack>
              </Toolbar>
            </Paper>
            <Box padding='15px'>
              <Typography variant='h6'>Basic Information</Typography>
              <Divider />
              <br />
              <Grid
                container
                component='dl'
                spacing={0}
                sx={{ display: 'table' }}
              >
                <Grid size='grow'>
                  <Typography
                    component='dt'
                    variant='body1'
                    sx={{ display: 'table-cell', width: '150px' }}
                  >
                    <b>Started:</b>
                  </Typography>
                  <Typography
                    component='dd'
                    variant='body2'
                    sx={{ display: 'table-cell' }}
                  >
                    {toNiceTimestamp(job.start, true)}
                  </Typography>
                </Grid>
                {job.status === STATUSES.STOPPED && job.stop ? (
                  <Grid size='grow'>
                    <Typography
                      component='dt'
                      variant='body1'
                      sx={{ display: 'table-cell', width: '150px' }}
                    >
                      <b>Stopped:</b>
                    </Typography>
                    <Typography
                      component='dd'
                      variant='body2'
                      sx={{ display: 'table-cell' }}
                    >
                      {toNiceTimestamp(job.stop, true)}
                    </Typography>
                  </Grid>
                ) : undefined}
                {job.status === STATUSES.FINISHED && job.end ? (
                  <Grid size='grow'>
                    <Typography
                      component='dt'
                      variant='body1'
                      sx={{ display: 'table-cell', width: '150px' }}
                    >
                      <b>Ended:</b>
                    </Typography>
                    <Typography
                      component='dd'
                      variant='body2'
                      sx={{ display: 'table-cell' }}
                    >
                      {toNiceTimestamp(job.end, true)}
                    </Typography>
                  </Grid>
                ) : undefined}
                <Grid size='grow'>
                  <Typography
                    component='dt'
                    variant='body1'
                    sx={{ display: 'table-cell', width: '150px' }}
                  >
                    <b>Status:</b>
                  </Typography>
                  <Typography
                    component='dd'
                    variant='body2'
                    sx={{ display: 'table-cell' }}
                  >
                    {job.status}
                  </Typography>
                </Grid>
                {job.resources && job.resources.gpu_type_id ? (
                  <Grid size='grow'>
                    <Typography
                      component='dt'
                      variant='body1'
                      sx={{ display: 'table-cell', width: '150px' }}
                    >
                      <b>GPU Type:</b>
                    </Typography>
                    <Typography
                      component='dd'
                      variant='body2'
                      sx={{ display: 'table-cell' }}
                    >
                      {getGpuTypeName(job.resources.gpu_type_id, gpuClasses)}
                    </Typography>
                  </Grid>
                ) : undefined}
                {job.resources && job.resources.gpu_count !== undefined ? (
                  <Grid size='grow'>
                    <Typography
                      component='dt'
                      variant='body1'
                      sx={{ display: 'table-cell', width: '150px' }}
                    >
                      <b>GPU Count:</b>
                    </Typography>
                    <Typography
                      component='dd'
                      variant='body2'
                      sx={{ display: 'table-cell' }}
                    >
                      {job.resources.gpu_count}
                    </Typography>
                  </Grid>
                ) : undefined}
                {job.credits !== undefined ? (
                  <Grid size='grow'>
                    <Typography
                      component='dt'
                      variant='body1'
                      sx={{ display: 'table-cell', width: '150px' }}
                    >
                      <b>Credits:</b>
                    </Typography>
                    <Typography
                      component='dd'
                      variant='body2'
                      sx={{ display: 'table-cell' }}
                    >
                      {job.credits}
                    </Typography>
                  </Grid>
                ) : undefined}
              </Grid>
            </Box>
            <Divider />
            <br />
            {[JOB_TYPES.TRAINING.value, JOB_TYPES.INFERENCE.value].includes(
              job.type,
            ) ? (
              <Box padding='15px'>
                {job.workers &&
                job.workers.filter(
                  (worker) => worker != null && worker.job_worker_uuid,
                ).length > 1 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Command</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {job.workers
                          .filter(
                            (worker) =>
                              worker != null && worker.job_worker_uuid,
                          )
                          .map((worker, i) => {
                            return (
                              <JobWorker
                                number={i + 1}
                                key={worker.job_worker_uuid}
                                job_uuid={job.job_uuid}
                                worker={worker}
                              />
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : job.workers &&
                  job.workers.filter(
                    (worker) => worker != null && worker.job_worker_uuid,
                  ).length === 1 ? (
                  <Typography variant='body1'>
                    <b>Command:</b>{' '}
                    {job.workers.find(
                      (worker) => worker != null && worker.job_worker_uuid,
                    )?.command || ''}
                  </Typography>
                ) : undefined}
              </Box>
            ) : [
                JOB_TYPES.ENDPOINT.value,
                JOB_TYPES.LLM.value,
                JOB_TYPES.GUARDRAIL.value,
              ].includes(job.type) &&
              job.status === STATUSES.RUNNING &&
              job.endpoint ? (
              <Box padding='15px'>
                <Typography variant='h6'>Endpoint Address</Typography>
                <br />
                <Stack direction='row' spacing={2} alignItems='center'>
                  <Typography
                    component='code'
                    sx={{
                      fontFamily: 'monospace',
                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                      padding: '4px 8px',
                      borderRadius: '4px',
                    }}
                  >
                    {job.endpoint.url}
                  </Typography>
                  <Button
                    size='small'
                    startIcon={<ContentCopyIcon />}
                    onClick={() => copyCodeToClipboard(job.endpoint.url)}
                  >
                    Copy
                  </Button>
                  {copySuccess ? (
                    <Typography variant='body2' color='success.main'>
                      Success!
                    </Typography>
                  ) : undefined}
                </Stack>
              </Box>
            ) : undefined}
          </Grid>
        </Grid>
      </Paper>
      <ErrorSnackBar message={api_error} />
      <NoLogsModal open={log_error} clearModal={() => setLogError(false)} />
    </Container>
  );
}
